import {
  linkClass,
  SiteUrlsContext,
  TypedBodyCellProps
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { History, Location } from 'history';
import * as React from 'react';
import { Link, Route } from 'react-router-dom';

import { linkCellClass } from './LinkCellFormatter';

/**
 * Preserves the query string (the part of the URL starting with ?).
 */
export function createQueryPreservingLinkCellFormatter<T>(
  getNameFromRowData: (rowData: T) => string,
  getPathFromRowData: (urls: SiteUrls, rowData: T) => string,
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => (
    <SiteUrlsContext.Consumer>
      {urls =>
        <QueryPreservingLinkCellFormatter
          pathname={getPathFromRowData(urls, props.row.data)}
          name={getNameFromRowData(props.row.data)}
        />
      }
    </SiteUrlsContext.Consumer>
  );
}

/**
 * Same as createQueryPreservingLinkCellFormatter, except uses history.replace instead of history.push.
 */
export function createQueryPreservingReplacingLinkCellFormatter<T>(
  getNameFromRowData: (rowData: T) => string,
  getPathFromRowData: (urls: SiteUrls, rowData: T) => string,
): React.Factory<TypedBodyCellProps<T>> {
  return (props: TypedBodyCellProps<T>) => (
    <SiteUrlsContext.Consumer>
      {urls =>
        <QueryPreservingReplacingLinkCellFormatter
          pathname={getPathFromRowData(urls, props.row.data)}
          name={getNameFromRowData(props.row.data)}
        />
      }
    </SiteUrlsContext.Consumer>
  );
}

type QueryPreservingLinkCellFormatterProps = {
  readonly name: string;
  readonly pathname: string;
};

function QueryPreservingLinkCellFormatter(props: QueryPreservingLinkCellFormatterProps) {
  return (
    <div className={linkCellClass}>
      <Route
        render={({ location }) =>
          <Link
            className={linkClass}
            to={{ ...location, pathname: props.pathname }}
            title={props.name}
          >
            {props.name}
          </Link>
        }
      />
    </div>
  );
}

type QueryPreservingReplacingLinkCellFormatterProps = QueryPreservingLinkCellFormatterProps;

class QueryPreservingReplacingLinkCellFormatter
  extends React.Component<QueryPreservingReplacingLinkCellFormatterProps> {

  public render(): JSX.Element {
    return (
      <Route
        render={({ location, history }) =>
          <QueryPreservingReplacingLinkCellFormatterInternal
            name={this.props.name}
            pathname={this.props.pathname}
            location={location}
            history={history}
          />
        }
      />
    );
  }

}

type QueryPreservingReplacingLinkCellFormatterInternalProps = QueryPreservingReplacingLinkCellFormatterProps & {
  readonly history: History;
  readonly location: Location;
};

/**
 * To implement a Link variant that uses history.replace instead of history.push,
 * all we should need to do is pass replace={true} to Link.
 * This works in the example, but fails in vizportalclient, for some poorly understood interaction
 * with other URL handling in that environment (likely related to Angular UI Router).
 * To work around this problem, we add a raw (non-React) event handler to the anchor
 * element and use it to call preventDefault on the click event.
 * This avoids whatever in vizportalclient was causing history.replace to not work.
 * We then implement a version of Link's (React) click handler that runs even when preventDefault
 * has been called, copying the relevant checks (for mouse button and modifier keys).
 * It may be possible to remove the extra complexity in the future,
 * maybe once Anugular UI Router is removed from vizportalclient.
 */
class QueryPreservingReplacingLinkCellFormatterInternal
  extends React.Component<QueryPreservingReplacingLinkCellFormatterInternalProps> {

  private anchorRef: HTMLAnchorElement | null = null;

  public render(): JSX.Element {
    return (
      <div className={linkCellClass}>
        <Link
          className={linkClass}
          to={{ ...this.props.location, pathname: this.props.pathname }}
          title={this.props.name}
          replace={true}
          innerRef={this.setAnchorRef}
          onClick={this.handleClick}
        >
          {this.props.name}
        </Link>
      </div>
    );
  }

  private readonly handleClick = (event: React.MouseEvent) => {
    if (event.button === 0 && !isModifiedEvent(event)) {
      event.preventDefault();
      this.props.history.replace({ ...this.props.location, pathname: this.props.pathname });
    }
  }

  private readonly setAnchorRef = (anchorRef: HTMLAnchorElement | null) => {
    if (this.anchorRef) {
      this.anchorRef.removeEventListener('click', preventDefaultHandler);
      this.anchorRef = null;
    }
    if (anchorRef) {
      this.anchorRef = anchorRef;
      this.anchorRef.addEventListener('click', preventDefaultHandler);
    }
  }

}

function isModifiedEvent(event: React.MouseEvent) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

function preventDefaultHandler(event: Event) {
  event.preventDefault();
}
